import { Alert, Box, Chip, Paper, Stack, Typography } from '@mui/material';
import { Product } from '../../types/product';

/** One property, shown only when the product actually carries it. */
const Property = ({ label, value }: { label: string; value?: unknown }) =>
  value === undefined || value === null || value === '' ? null : (
    <Box>
      <Typography variant="caption" color="text.secondary" display="block">
        {label}
      </Typography>
      <Typography variant="body2" fontWeight={600}>
        {String(value)}
      </Typography>
    </Box>
  );

/**
 * What the chosen product actually is - colour, memory, barcode, prices, stock.
 *
 * <p>Shown while buying and while billing, because a name is not enough to be sure:
 * a shop can easily hold the same model in two colours or two memory sizes under
 * names that read almost alike, and the mistake only shows up when the customer
 * opens the box. The properties are on screen before the line is added, so the
 * person entering it can check against the carton in their hand.
 *
 * <p>When another product shares the name, that is said out loud rather than left
 * for them to notice.
 */
const ProductProperties = ({ product, sameName = 0 }: { product: Product; sameName?: number }) => (
  <Box>
    <Stack direction="row" alignItems="center" gap={1} flexWrap="wrap" sx={{ mb: 1 }}>
      <Typography fontWeight={700}>{product.productName}</Typography>
      <Chip
        size="small"
        variant="outlined"
        label={product.serialTracked === false ? 'No serial number · counted by quantity' : 'Tracked by serial number'}
      />
      <Chip
        size="small"
        variant="outlined"
        color={Number(product.availableStock) > 0 ? 'success' : 'default'}
        label={`${product.availableStock ?? 0} in stock`}
      />
    </Stack>

    {sameName > 1 && (
      <Alert severity="warning" sx={{ mb: 1.5, py: 0 }}>
        {sameName} products share this name. Check the colour, memory and barcode below before adding the line.
      </Alert>
    )}

    <Stack direction="row" flexWrap="wrap" gap={3} rowGap={1.5}>
      <Property label="Brand" value={product.brandName} />
      <Property label="Model" value={product.modelName} />
      <Property label="Colour" value={product.colour} />
      <Property label="RAM" value={product.ram} />
      <Property label="Storage" value={product.storage} />
      <Property label="Barcode" value={product.barcode} />
      <Property label="HSN" value={product.hsnCode} />
      <Property label="UOM" value={product.uom} />
      <Property label="GST %" value={product.gstPercentage} />
      <Property label="MRP" value={product.mrp} />
      <Property label="Current price" value={product.sellingPrice} />
    </Stack>

    {(product.specs?.length ?? 0) > 0 && (
      <Paper variant="outlined" sx={{ mt: 1.5, p: 1, bgcolor: 'transparent' }}>
        <Stack direction="row" flexWrap="wrap" gap={0.75}>
          {product.specs?.map((spec) => (
            <Chip key={spec.id ?? spec.specName} size="small" label={`${spec.specName}: ${spec.specValue ?? '-'}`} />
          ))}
        </Stack>
      </Paper>
    )}
  </Box>
);

export default ProductProperties;
